import React, { useState, ReactNode, useEffect, useRef, MutableRefObject } from 'react';
import { Shortcut } from '../Shortcut/Shortcut';
import classnames from 'classnames';

import './SelectableList.css';
import GlobalProps from '../globalProps';
import { t } from '../../locales/translate';

interface Props extends GlobalProps {
  options: ReactNode[];
  unSelectableFooter?: ReactNode;
  resetHighlight?: boolean;
  displayEmpty?: boolean;
  onSelect?: (index: number) => void;
}

export function SelectableList({
  className,
  style,
  options,
  unSelectableFooter,
  resetHighlight = true,
  displayEmpty,
  onSelect,
}: Props): JSX.Element {
  const listRef: MutableRefObject<HTMLUListElement | null> = useRef(null);
  const [highlightedOption, setHighlightedOption] = useState<number | undefined>(0);

  useEffect(() => {
    resetHighlight && setHighlightedOption(0);
  }, [options]);

  function scrollToSelectedItem(): void {
    const selected = listRef?.current?.querySelector('.focus');
    selected?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }

  function highlightOption(direction: 'up' | 'down' | number): void {
    if (direction === 'up') {
      if (!highlightedOption) {
        setHighlightedOption(options.length - 1);
      } else {
        setHighlightedOption(highlightedOption - 1);
      }
      scrollToSelectedItem();
    } else if (direction === 'down') {
      if (highlightedOption === undefined || highlightedOption === options.length - 1) {
        setHighlightedOption(0);
      } else if (highlightedOption < options.length) {
        setHighlightedOption(highlightedOption + 1);
      }
      scrollToSelectedItem();
    } else {
      setHighlightedOption(direction);
    }
  }

  return (
    <>
      <ul className={classnames('selectable-list', className)} style={style} ref={listRef}>
        {!options.length && displayEmpty && (
          <li className="selectable-list__item selectable-list__no-results">{t.errors.noResults}</li>
        )}
        {options.map((option, index) => (
          <li
            key={index}
            className={classnames('selectable-list__item', {
              focus: highlightedOption === index,
            })}
            onClick={() => {
              if (onSelect) {
                onSelect(index);
              }
            }}
            onMouseEnter={() => {
              highlightOption(index);
            }}
          >
            {option}
          </li>
        ))}
        {unSelectableFooter && <li className="selectable-list__footer">{unSelectableFooter}</li>}
      </ul>
      {highlightedOption !== undefined && (
        <>
          <Shortcut
            keys="Enter"
            onInvoke={() => {
              if (onSelect) {
                onSelect(highlightedOption);
              }
            }}
            overrideInput={true}
          />
        </>
      )}
      <Shortcut
        keys="ArrowUp"
        onInvoke={() => {
          highlightOption('up');
        }}
        overrideInput={true}
      />
      <Shortcut
        keys="ArrowDown"
        onInvoke={() => {
          highlightOption('down');
        }}
        overrideInput={true}
      />
    </>
  );
}
